import { getLogger } from "@expert/logging";
import { expertWorkspaceWebSocketEventBus } from "@expert/websocket";
import { useEffect } from "react";

import { useChatStore } from "@/sdk";
import { userCache } from "@/shared-utils";

export interface NeglectionEventPayload {
    chatId: string;
    expertEmployeeId: string;
    durationSeconds?: number;
    startTime?: number;
    endTime?: number;
}

// Module-scoped (not the React context logger): this handles events for ANY of the expert's chats,
// so inheriting the active chat's identity would mis-attribute background events under concurrent
// chats. Identity is bound per-event from the chat the event is about instead.
const logger = getLogger({ module: "useNeglectionEventListener", supportTeam: "messaging" });

export const useNeglectionEventListener = () => {
    useEffect(() => {
        return expertWorkspaceWebSocketEventBus.on("ws_chat-neglection-event", (payload: NeglectionEventPayload) => {
            const { chatId, expertEmployeeId, startTime, durationSeconds, endTime } = payload;

            // For Connect chat, chatId === sessionId === contactId, so the event's own chatId is the
            // correlation id — no session lookup needed.
            const eventLogger = logger.child({ chatId, sessionId: chatId });

            eventLogger.info({ neglectionEventPayload: JSON.stringify(payload) }, "Neglection event received");

            if (expertEmployeeId !== userCache.employeeId) {
                eventLogger.debug(
                    { expertEmployeeId, userCacheEmployeeId: userCache.employeeId },
                    "Ignoring neglection event for different expert",
                );
                return;
            }

            // If no endTime, neglection is starting/active
            if (endTime) {
                useChatStore.getState().clearChatNeglection(chatId);
            } else if (startTime) {
                useChatStore.getState().updateChatNeglection(chatId, { startTime, durationSeconds });
            }
        });
    }, []);
};
